import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";
import type { MidiaDaBibliotecaDto } from "@base/contracts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PickerBiblioteca } from "./picker-biblioteca";

type Props = {
  readonly aberto: boolean;
  readonly itens: readonly MidiaDaBibliotecaDto[];
  readonly selecionados: readonly string[];
  readonly carregando?: boolean;
  readonly onAlternar: (id: string) => void;
  readonly onConfirmar: () => void;
  readonly onFechar: () => void;
};

export function ModalEscolherBiblioteca({
  aberto,
  itens,
  selecionados,
  carregando = false,
  onAlternar,
  onConfirmar,
  onFechar,
}: Props) {
  const [busca, definirBusca] = useState("");

  useEffect(() => {
    if (!aberto) return;
    function aoTecla(evento: KeyboardEvent) {
      if (evento.key === "Escape") onFechar();
    }
    document.addEventListener("keydown", aoTecla);
    return () => document.removeEventListener("keydown", aoTecla);
  }, [aberto, onFechar]);

  const visiveis = useMemo(() => {
    const q = busca.trim().toLocaleLowerCase("pt-BR");
    if (!q) return itens;
    return itens.filter((item) =>
      item.nome.toLocaleLowerCase("pt-BR").includes(q),
    );
  }, [busca, itens]);

  if (!aberto || typeof document === "undefined") return null;

  const rotulo =
    selecionados.length === 1
      ? "Usar este arquivo"
      : `Usar ${selecionados.length} arquivos`;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-0 sm:p-4"
      style={{ zIndex: "var(--rk-z-toast)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-biblioteca-titulo"
    >
      <button
        type="button"
        aria-label="Fechar"
        className="absolute inset-0 cursor-pointer bg-overlay backdrop-blur-[6px]"
        onClick={onFechar}
      />
      <div className="relative z-[1] flex h-full w-full max-w-[1080px] flex-col overflow-hidden bg-popover sm:h-[min(90dvh,840px)] sm:rounded-2xl sm:border sm:border-border-subtle sm:shadow-e3">
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border-subtle px-6 py-5">
          <div className="min-w-0 space-y-1">
            <h2
              id="modal-biblioteca-titulo"
              className="text-xl font-semibold leading-7 tracking-[-0.01em] text-foreground"
            >
              Escolher da biblioteca
            </h2>
            <p className="text-[13px] text-muted-foreground">
              Só entram arquivos que servem para este post e essas redes.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="shrink-0"
            aria-label="Fechar"
            onClick={onFechar}
          >
            <X className="size-4" aria-hidden />
          </Button>
        </header>

        <div className="shrink-0 px-5 pt-5">
          <div className="relative">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              type="search"
              value={busca}
              onChange={(evento) => definirBusca(evento.target.value)}
              placeholder="Buscar pelo nome"
              aria-label="Buscar na biblioteca"
              className="pl-9"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          <PickerBiblioteca
            itens={visiveis}
            selecionados={selecionados}
            carregando={carregando}
            variante="grande"
            onAlternar={onAlternar}
          />
        </div>

        <footer className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border-subtle bg-[color-mix(in_srgb,var(--popover)_92%,var(--sunken))] px-6 py-4">
          <p className="text-sm text-muted-foreground">
            {selecionados.length === 0
              ? "Nenhum arquivo escolhido"
              : selecionados.length === 1
                ? "1 arquivo escolhido"
                : `${selecionados.length} arquivos escolhidos`}
          </p>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              className="cursor-pointer"
              onClick={onFechar}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="cursor-pointer"
              disabled={selecionados.length === 0}
              onClick={onConfirmar}
            >
              {rotulo}
            </Button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
